/**
 * @file CollapsibleTierHeaderView.tsx
 * @description Pressable tier header with chevron icon indicating
 * expand/collapse state. Extracted as a separate component to avoid
 * inline arrow functions in JSX (react-perf/jsx-no-new-function-as-prop).
 * @module features/squad-planning/components/CollapsibleTierHeader
 */

import { ChevronDown, ChevronUp } from "lucide-react-native";
import type { ReactNode } from "react";
import { memo } from "react";
import { Box } from "@/components/atoms/Box";

import { Stack } from "@/components/atoms/Stack";
import { Icon } from "@/components/atoms/Icon";
import { Pressable } from "@/components/atoms/Pressable";
import { Text } from "@/components/atoms/Text";

/**
 * How the header dresses itself. `standalone` (default) draws its own outline
 * and the uppercase eyebrow the contracts and squad-planning mounts use.
 * `nested` is the Pitch's current-squad panel (SE-8486, frame 558:25108):
 * the parent draws the single border, so the row keeps only a bottom rule,
 * and the tier name reads in sentence case in the secondary tone.
 */
export type CollapsibleTierHeaderAppearance = "standalone" | "nested";

/** Props for {@link CollapsibleTierHeaderView} */
export interface CollapsibleTierHeaderViewProps {
  /** Display name of the tier */
  readonly tierName: string;
  /** Number of players in this tier */
  readonly count: number;
  /** Whether the tier content is currently hidden */
  readonly isCollapsed: boolean;
  /** Pre-bound press handler (no inline arrow needed) */
  readonly onPress: () => void;
  /** Optional action element rendered on the right side (e.g. CTA button) */
  readonly actionSlot?: ReactNode;
  /** Outline and label treatment; `standalone` when omitted. */
  readonly appearance?: CollapsibleTierHeaderAppearance;
}

/** Row outline per appearance, open (content below) and closed. */
const ROW_CLASS: Readonly<
  Record<CollapsibleTierHeaderAppearance, { open: string; closed: string }>
> = {
  standalone: {
    open: "rounded-t-sm border border-outline-default bg-surface-raised",
    closed: "rounded-t-sm rounded-b-md border border-outline-default",
  },
  // The parent draws the outline; the rule under the row exists only while
  // rows follow it, so a collapsed tier does not double the parent border.
  nested: {
    open: "border-b border-outline-subtle bg-surface-raised",
    closed: "",
  },
};

/** Tier-name treatment per appearance. */
const LABEL_CLASS: Readonly<Record<CollapsibleTierHeaderAppearance, string>> = {
  standalone: "uppercase tracking-widest text-content-primary",
  nested: "text-content-secondary",
};

/**
 * Count pill per appearance. `standalone` keeps the pill the unmeasured
 * consumers (contracts, PlayerPanel, ActionGroupCard) always had; `nested` is
 * the frame's 24×20 pill on the subtle surface (SE-8486, W63).
 */
const PILL_CLASS: Readonly<Record<CollapsibleTierHeaderAppearance, string>> = {
  standalone: "bg-surface-muted px-2",
  // px-1: with px-2 a two-digit count measured 28 wide on the build; the
  // frame's pill is 24, which min-w-6 already guarantees.
  nested: "min-w-6 bg-surface-subtle px-1",
};

/**
 * Renders a pressable row with a chevron, tier name, player count,
 * and an optional action slot on the right for tier-specific CTAs.
 * @param props - Component props
 * @param props.tierName - Tier display label
 * @param props.count - Player count badge
 * @param props.isCollapsed - Controls which chevron direction is shown
 * @param props.onPress - Stable callback bound in the container
 * @param props.actionSlot - Optional action element on the right side
 * @param props.appearance - Outline and label treatment
 * @returns A pressable header row for a tier section
 */
const CollapsibleTierHeaderView = ({
  tierName,
  count,
  isCollapsed,
  onPress,
  actionSlot,
  appearance = "standalone",
}: CollapsibleTierHeaderViewProps) => (
  <Stack
    testID={`tier-header-row-${tierName}`}
    direction="horizontal"
    className={`h-10 shrink-0 items-center justify-between px-3 ${
      ROW_CLASS[appearance][isCollapsed || count === 0 ? "closed" : "open"]
    }`}
  >
    <Pressable
      testID={`tier-header-${tierName}`}
      onPress={onPress}
      className="flex-1 flex-row items-center gap-3"
    >
      <Icon
        as={isCollapsed ? ChevronUp : ChevronDown}
        className="size-3 text-content-subtle"
      />
      <Text variant="micro-strong" className={LABEL_CLASS[appearance]}>
        {tierName}
      </Text>
      <Box
        testID={`tier-header-count-${tierName}`}
        className={`h-5 items-center justify-center rounded-full ${PILL_CLASS[appearance]}`}
      >
        <Text variant="micro-strong" className="text-content-muted">
          {count}
        </Text>
      </Box>
    </Pressable>
    {actionSlot != null ? <Box>{actionSlot}</Box> : null}
  </Stack>
);

CollapsibleTierHeaderView.displayName = "CollapsibleTierHeaderView";

export default memo(CollapsibleTierHeaderView);
